/**
 * Convert live detector outputs (FE ArtifactDetectionService / BE ArtifactDetector)
 * into ClassifiedArtifact overlay records.
 */

import {
  mapDetectorTypeToOverlay,
  type ClassifiedArtifact,
} from "./artifactClassification";

export type LiveDetectorArtifactLike = {
  artifactId?: string;
  id?: string;
  type: string;
  timestamp?: number;
  startTime?: number;
  endTime?: number;
  duration?: number;
  channels?: string[];
  channelName?: string;
  confidence?: number;
  subtype?: string;
  description?: string;
};

export function liveDetectorArtifactToOverlay(
  detection: LiveDetectorArtifactLike,
  index = 0,
): ClassifiedArtifact | null {
  const mapped =
    mapDetectorTypeToOverlay(detection.type) ||
    mapDetectorTypeToOverlay(detection.subtype || "") ||
    mapDetectorTypeToOverlay(detection.description || "");
  if (!mapped) return null;

  // FE ArtifactDetection + BE ArtifactDetector both express times in ms.
  const startMs =
    typeof detection.timestamp === "number"
      ? detection.timestamp
      : typeof detection.startTime === "number"
        ? detection.startTime
        : 0;
  const endMs =
    typeof detection.endTime === "number"
      ? detection.endTime
      : startMs +
        (typeof detection.duration === "number" ? detection.duration : 250);
  const startTimeSeconds = startMs / 1000;
  const endTimeSeconds = Math.max(startTimeSeconds + 0.001, endMs / 1000);
  const channels =
    Array.isArray(detection.channels) && detection.channels.length
      ? detection.channels
      : detection.channelName
        ? [detection.channelName]
        : [];

  return {
    id:
      detection.artifactId ||
      detection.id ||
      `live-${mapped}-${index}-${startTimeSeconds.toFixed(3)}`,
    type: mapped,
    startTimeSeconds,
    endTimeSeconds,
    channels,
    confidence:
      typeof detection.confidence === "number" ? detection.confidence : 0.7,
    label: `${mapped} (live)`,
  };
}

export function mergeArtifactOverlays(
  ...groups: ClassifiedArtifact[][]
): ClassifiedArtifact[] {
  const byKey = new Map<string, ClassifiedArtifact>();
  for (const group of groups) {
    for (const artifact of group) {
      const key = [
        artifact.type,
        artifact.startTimeSeconds.toFixed(2),
        artifact.channels.join(","),
      ].join("|");
      const existing = byKey.get(key);
      if (!existing || artifact.confidence > existing.confidence) {
        byKey.set(key, artifact);
      }
    }
  }
  return Array.from(byKey.values()).sort(
    (a, b) => a.startTimeSeconds - b.startTimeSeconds,
  );
}
